import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import EmptyState from '@/components/base/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import ModuleModal from './components/ModuleModal';

interface SaasModule {
  id: string; name: string; description: string | null; category: string;
  is_core: boolean; is_addon: boolean; default_enabled: boolean; price_monthly: number;
  is_active: boolean; created_at: string;
}

const MODULE_ICONS: Record<string, string> = {
  clients: 'ri-user-3-line', contracts: 'ri-file-text-line', payments: 'ri-bank-card-line',
  jobs: 'ri-briefcase-line', public_website: 'ri-global-line', client_portal: 'ri-user-shared-line',
  basic_branding: 'ri-palette-line', digital_invitations: 'ri-mail-send-line', rsvp: 'ri-calendar-check-line',
  media_packages: 'ri-folder-image-line', dvd_online: 'ri-disc-line', photo_selection: 'ri-image-line',
  final_deliveries: 'ri-send-plane-line', ai_sales_assistant: 'ri-robot-line', appointments: 'ri-calendar-line',
  whatsapp_notifications: 'ri-whatsapp-line', email_automations: 'ri-mail-settings-line',
  custom_domain: 'ri-earth-line', extra_storage: 'ri-hard-drive-line',
};

const CAT_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  Production: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'text-emerald-600' },
  Website:    { bg: 'bg-cyan-50',    text: 'text-cyan-600',    label: 'text-cyan-600' },
  Automation: { bg: 'bg-amber-50',   text: 'text-amber-600',   label: 'text-amber-600' },
  Settings:   { bg: 'bg-rose-50',    text: 'text-rose-600',    label: 'text-rose-600' },
};

const CATEGORIES = ['All', 'Production', 'Website', 'Automation', 'Settings'];
const TYPE_TABS  = ['All', 'Core', 'Add-ons'];

export default function SAModulesPage() {
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [modules, setModules]   = useState<SaasModule[]>([]);
  const [typeFilter, setTypeFilter] = useState('All');
  const [catFilter, setCatFilter]   = useState('All');
  const [search, setSearch]         = useState('');
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState<SaasModule | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const { data, error: err } = await supabase.from('saas_modules').select('*').order('category').order('name');
    if (err) { setError(err.message); return; }
    setModules(data ?? []);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await load();
      setLoading(false);
    };
    init();
  }, [navigate, load]);

  const toggleActive = async (mod: SaasModule) => {
    const newVal = !mod.is_active;
    setModules((prev) => prev.map((m) => m.id === mod.id ? { ...m, is_active: newVal } : m));
    await supabase.from('saas_modules').update({ is_active: newVal }).eq('id', mod.id);
  };

  const openNew  = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (m: SaasModule) => { setEditing(m); setModalOpen(true); };

  const filtered = modules.filter((m) => {
    const matchType   = typeFilter === 'All' || (typeFilter === 'Core' ? m.is_core : m.is_addon);
    const matchCat    = catFilter === 'All' || m.category === catFilter;
    const matchSearch = !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.id.includes(search.toLowerCase());
    return matchType && matchCat && matchSearch;
  });

  const stats = {
    total: modules.length, core: modules.filter((m) => m.is_core).length,
    addons: modules.filter((m) => m.is_addon).length, active: modules.filter((m) => m.is_active).length,
  };

  if (loading) return <SuperAdminLayout><SAHeader title="Modules" subtitle="Catálogo global de módulos del SaaS" /><LoadingState /></SuperAdminLayout>;
  if (error)   return <SuperAdminLayout><SAHeader title="Modules" subtitle="Catálogo global de módulos del SaaS" /><ErrorState message={error ?? undefined} onRetry={load} /></SuperAdminLayout>;

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Modules"
        subtitle="Catálogo global de módulos del SaaS"
        actions={
          <button onClick={openNew} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
            Nuevo módulo
          </button>
        }
      />
      <div className="px-4 md:px-8 py-4 md:py-7 space-y-5">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total',   value: stats.total,  icon: 'ri-apps-line',         color: 'text-slate-900', bg: 'bg-slate-100' },
            { label: 'Core',    value: stats.core,   icon: 'ri-shield-check-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Add-ons', value: stats.addons, icon: 'ri-puzzle-line',       color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Activos', value: stats.active, icon: 'ri-toggle-line',       color: 'text-blue-600', bg: 'bg-blue-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.bg} ${s.color} shrink-0`}><i className={`${s.icon} text-base`}></i></span>
              <div><p className={`text-xl font-bold ${s.color}`}>{s.value}</p><p className="text-slate-500 text-xs">{s.label}</p></div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <div className="flex bg-white border border-slate-200 rounded-lg p-1 gap-1">
              {TYPE_TABS.map((t) => (
                <button key={t} onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${typeFilter === t ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCatFilter(c)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${catFilter === c ? 'bg-blue-50 text-blue-700 border-blue-200' : 'text-slate-500 border-slate-200 hover:text-slate-900 hover:border-slate-300'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="relative w-full sm:w-48">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400"><i className="ri-search-line text-sm"></i></span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar módulo..."
              className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-400" />
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <EmptyState icon="ri-puzzle-line" title="No hay módulos"
            description={search || typeFilter !== 'All' || catFilter !== 'All' ? 'Prueba con otros filtros.' : 'Crea el primer módulo del catálogo.'}
            actions={(!search && typeFilter === 'All' && catFilter === 'All') ? [{ label: 'Nuevo módulo', onClick: openNew }] : []} />
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left text-slate-500 text-xs font-medium px-5 py-3 uppercase tracking-wider">Módulo</th>
                    <th className="text-left text-slate-500 text-xs font-medium px-4 py-3 uppercase tracking-wider">Categoría</th>
                    <th className="text-left text-slate-500 text-xs font-medium px-4 py-3 uppercase tracking-wider">Tipo</th>
                    <th className="text-left text-slate-500 text-xs font-medium px-4 py-3 uppercase tracking-wider">Precio</th>
                    <th className="text-center text-slate-500 text-xs font-medium px-4 py-3 uppercase tracking-wider">Default</th>
                    <th className="text-center text-slate-500 text-xs font-medium px-4 py-3 uppercase tracking-wider">Activo</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((mod) => {
                    const catStyle = CAT_STYLE[mod.category] ?? { bg: 'bg-slate-50', text: 'text-slate-500', label: 'text-slate-500' };
                    const icon     = MODULE_ICONS[mod.id] ?? 'ri-apps-line';
                    return (
                      <tr key={mod.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${catStyle.bg} ${catStyle.text}`}><i className={`${icon} text-sm`}></i></span>
                            <div><p className="text-slate-900 text-sm font-medium">{mod.name}</p><p className="text-slate-400 text-xs font-mono">{mod.id}</p></div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2 py-1 rounded-md text-[11px] font-medium ${catStyle.bg} ${catStyle.label}`}>{mod.category}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          {mod.is_core
                            ? <span className="px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700">Core</span>
                            : <span className="px-2 py-1 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700">Add-on</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          {mod.price_monthly === 0
                            ? <span className="text-slate-300 text-sm">—</span>
                            : <span className="text-slate-900 text-sm font-medium">${mod.price_monthly}<span className="text-slate-400 text-xs">/mo</span></span>}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          {mod.default_enabled
                            ? <span className="w-5 h-5 inline-flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><i className="ri-check-line text-xs"></i></span>
                            : <span className="text-slate-300 text-sm">—</span>}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <button onClick={() => toggleActive(mod)}
                            className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${mod.is_active ? 'bg-blue-600' : 'bg-slate-200'}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${mod.is_active ? 'left-5' : 'left-0.5'}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <button onClick={() => openEdit(mod)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-slate-100"><p className="text-slate-400 text-xs">{filtered.length} de {modules.length} módulos</p></div>
          </div>
        )}
      </div>
      {modalOpen && <ModuleModal module={editing} onClose={() => setModalOpen(false)} onSaved={load} />}
    </SuperAdminLayout>
  );
}
